import { useCallback, useEffect, useRef } from 'react';
import { GetCallsResponse } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { InboxThreadItemTagAlert } from '@weave/schema-gen-ts/dist/shared/notification/alerts.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { CallRecordEventType } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import {
  CallRecordTagNotification,
  CallRecordUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { isEqual } from 'lodash-es';
import { InfiniteData, useQueryClient } from 'react-query';
import { CallIntelTypes, CallIntelligenceV2 } from '@frontend/api-call-intel';
import { SchemaQueryKey } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { useWebsocketEventSubscription } from '@frontend/websocket';

type Props = {
  enabled: boolean;
  activeQueryKey: SchemaQueryKey<CallIntelligenceV2.Types.ServiceInfiniteQueries, 'ListCalls'>;
  isInfiniteList?: boolean;
  currentPage?: number;
  pageSize: number;
  isCallIntelAnalysisFiltersApplied: boolean;
  listCallsPayload: CallIntelligenceV2.Types.ListCallsIO['input'];
  onTagUpdate?: (channelId: string, tagIds: string[]) => void;
};

/**
 * Events that can be queued while a filter-match fetch
 * is in-flight or while we're waiting for analysis to complete.
 */
type QueuedEvent =
  | { type: 'call_record'; event: CallRecordUpdateEvent }
  | { type: 'tag'; event: CallRecordTagNotification }
  | { type: 'inbox_tag'; event: InboxThreadItemTagAlert };

/**
 * Per-channelId state machine tracking the lifecycle of a new call record
 * from its initial websocket event through cache insertion.
 *
 * - `awaiting_analysis`: NEW event received but analysis filters are active,
 *   so we defer the filter-match fetch until FINISHED_PROCESSING.
 * - `fetching`: Filter-match API call is in-flight. Any subsequent events
 *   for this channelId are queued and replayed after the fetch resolves.
 */
type ChannelState = {
  status: 'awaiting_analysis' | 'fetching';
  queuedEvents: QueuedEvent[];
};

export const useCallRecordsWebsocketSubscription = ({
  enabled,
  activeQueryKey,
  currentPage,
  pageSize,
  isInfiniteList,
  isCallIntelAnalysisFiltersApplied,
  listCallsPayload,
  onTagUpdate,
}: Props) => {
  const queryClient = useQueryClient();
  const fetchListCalls = CallIntelligenceV2.Queries.useFetchListCalls();

  const channelStateMap = useRef<Map<string, ChannelState>>(new Map());

  const invalidateOtherListCallsQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        return (
          Array.isArray(queryKey) &&
          queryKey[0] === CallIntelligenceV2.serviceName &&
          queryKey[1] === ('ListCalls' as CallIntelligenceV2.Types.QueryEndpointName) &&
          !isEqual(queryKey, activeQueryKey)
        );
      },
    });
  }, [activeQueryKey, queryClient]);

  const invalidateCallMetadataQuery = useCallback(
    (channelId: string) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as SchemaQueryKey<
            CallIntelligenceV2.Types.ServiceQueries,
            'GetCallMetadataNew'
          >;
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === CallIntelligenceV2.serviceName &&
            queryKey[1] === ('GetCallMetadataNew' as CallIntelligenceV2.Types.QueryEndpointName) &&
            queryKey[2]?.id === channelId
          );
        },
      });
    },
    [queryClient]
  );

  const invalidateListTasksQueries = useCallback(
    (channelId: string) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as SchemaQueryKey<CallIntelTypes.TaskServiceQueries, 'ListTasks'>;
          return !!(
            Array.isArray(queryKey) &&
            queryKey[0] === CallIntelligenceV2.serviceName &&
            queryKey[1] === ('ListTasks' as keyof CallIntelTypes.TaskServiceQueries) &&
            queryKey[2]?.filter?.[CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE_ID]?.sourceId?.includes(channelId)
          );
        },
      });
    },
    [queryClient]
  );

  const updateCallInActiveCache = useCallback(
    (channelId: string, updater: (call: CallIntelTypes.CallRecord) => CallIntelTypes.CallRecord) => {
      queryClient.setQueryData<InfiniteData<GetCallsResponse> | undefined>(activeQueryKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }

        const updatedPages = oldData.pages.map((page) => {
          const updatedCalls = (page.newCalls ?? []).map((existingCall) => {
            if (existingCall.id === channelId) {
              return updater(existingCall as CallIntelTypes.CallRecord);
            }
            return existingCall;
          });

          return {
            ...page,
            newCalls: updatedCalls,
          };
        });

        return {
          ...oldData,
          pages: updatedPages,
        };
      });
    },
    [activeQueryKey, queryClient]
  );

  const upsertNewCallInActiveCache = useCallback(
    (newCall: CallIntelTypes.CallRecord) => {
      queryClient.setQueryData<InfiniteData<GetCallsResponse> | undefined>(activeQueryKey, (oldData) => {
        if (!oldData || oldData.pages.length === 0) {
          return oldData;
        }

        // Early duplicate check — avoid unnecessary allocations
        if (oldData.pages.some((page) => page.newCalls?.some((call) => call.id === newCall.id))) {
          return oldData;
        }

        const [firstPage, ...restPages] = oldData.pages;
        const mergedCalls = [newCall, ...(firstPage.newCalls ?? [])];

        const updatedPrivateCallsFlag = firstPage.hasPrivateCalls || newCall.isPrivate;
        const updatedFirstPage: GetCallsResponse = {
          ...firstPage,
          newCalls: mergedCalls,
          hasPrivateCalls: updatedPrivateCallsFlag,
        };

        // Case 1: Infinite list — prepend to first page, keep all other pages intact
        // Case 2: Paginated table but NOT on first page — prepend to first page, keep other pages/params intact
        //         We allow overflow in this case to avoid shifting data between pages.
        //         We add a special field CALL_LIST_PAGE_OVERFLOW_ALLOWED_FIELD to indicate the list-calls query to prevent slice the extra call records from page
        if (isInfiniteList || (currentPage ?? 1) > 1) {
          const firstPageNextCursor = firstPage.nextCursor;
          // Check if the firstPageNextCursor id matches the last record of first page
          // If not then remove the last record because this is an extra record added to check if next page exists
          // As we are are allowing page overflow in this case, we need to slice the extra record here
          if (firstPageNextCursor?.id && mergedCalls?.[mergedCalls.length - 1]?.id !== firstPageNextCursor.id) {
            updatedFirstPage.newCalls = mergedCalls?.slice(0, -1);
          }

          return {
            ...oldData,
            pages: [
              { ...updatedFirstPage, [CallIntelligenceV2.CALL_LIST_PAGE_OVERFLOW_ALLOWED_FIELD]: true },
              ...restPages,
            ],
          };
        }

        // Case 3: Paginated table, on first page
        // If adding the new record causes an overflow beyond the display page size,
        // we need to trim the list and update the nextCursor to maintain pagination integrity.
        if (mergedCalls.length > pageSize) {
          const limitedCalls = mergedCalls.slice(0, pageSize);
          updatedFirstPage.newCalls = limitedCalls;
          updatedFirstPage.hasPrivateCalls = limitedCalls.some((call) => call.isPrivate);

          const cursorRecord = limitedCalls[pageSize - 1];
          updatedFirstPage.nextCursor = cursorRecord?.id
            ? { id: cursorRecord.id, startTime: cursorRecord.startTime }
            : firstPage.nextCursor;
        }

        // Clear other pages and pageParams — the cursor chain is invalidated since data shifted
        return {
          ...oldData,
          pages: [updatedFirstPage],
          pageParams: [],
        };
      });
    },
    [activeQueryKey, currentPage, isInfiniteList, pageSize, queryClient]
  );

  /**
   * Processes queued events for a channelId after its call record has been
   * successfully inserted into the cache. Applies each queued update in order,
   * then removes the channelId from the state map.
   */
  const processQueuedEvents = useCallback(
    (channelId: string) => {
      const state = channelStateMap.current.get(channelId);
      if (!state) {
        return;
      }

      for (const queuedEvent of state.queuedEvents) {
        if (queuedEvent.type === 'call_record') {
          const { event } = queuedEvent;

          if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_FINISHED_PROCESSING) {
            const callIntelInsight = event.callIntelInsight;
            if (callIntelInsight?.channelId) {
              updateCallInActiveCache(callIntelInsight.channelId, (call) => ({
                ...call,
                ...callIntelInsight,
              }));
              invalidateCallMetadataQuery(callIntelInsight.channelId);
            }
          }

          if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_FOLLOWUP_TASKS_PROCESSED) {
            const tasksCount = event.tasksCount;
            if (tasksCount?.channelId) {
              updateCallInActiveCache(tasksCount.channelId, (call) => ({
                ...call,
                taskCount: tasksCount.count ?? 0,
              }));
              invalidateCallMetadataQuery(tasksCount.channelId);
              invalidateListTasksQueries(tasksCount.channelId);
            }
          }
        }

        if (queuedEvent.type === 'tag') {
          const { event } = queuedEvent;
          if (event.channelId) {
            const updatedTags = event.tagsPayload.map((tag) => tag.id);
            onTagUpdate?.(event.channelId, updatedTags);
          }
        }

        if (queuedEvent.type === 'inbox_tag') {
          const { event } = queuedEvent;
          const inboxTagChannelId = event.entityId;
          if (inboxTagChannelId) {
            const currentData = queryClient.getQueryData<InfiniteData<GetCallsResponse>>(activeQueryKey);
            const existingCall = currentData?.pages
              .flatMap((p) => p.newCalls ?? [])
              .find((c) => c.id === inboxTagChannelId);
            const currentTagIds = existingCall?.tagIds ?? [];
            const updatedTagIds =
              event.updateType === 'ADDED'
                ? Array.from(new Set([...currentTagIds, event.tagId]))
                : currentTagIds.filter((id) => id !== event.tagId);
            onTagUpdate?.(inboxTagChannelId, updatedTagIds);
          }
        }
      }

      // Invalidate other queries once after all queued events are processed
      invalidateOtherListCallsQueries();

      // Clean up — this channelId is fully handled
      channelStateMap.current.delete(channelId);
    },
    [
      activeQueryKey,
      queryClient,
      invalidateCallMetadataQuery,
      invalidateListTasksQueries,
      invalidateOtherListCallsQueries,
      onTagUpdate,
      updateCallInActiveCache,
    ]
  );

  /**
   * Fetches the new call record from the API using the current active filters,
   * inserts it into the cache if it matches, and then processes any queued events.
   * On failure, cleans up the channel state and discards queued events.
   */
  const fetchAndInsertCall = useCallback(
    async (channelId: string) => {
      const requestPayload = {
        ...listCallsPayload,
        filter: {
          ...listCallsPayload.filter,
          [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_ID]: {
            callId: [channelId],
          },
        },
        limit: 2, // We only need one record but api accepts page size + 1
      } as CallIntelTypes.ListCallsIO['input'];

      try {
        const response = (await fetchListCalls({
          request: requestPayload,
          options: { retry: false },
        })) as GetCallsResponse;

        const newCall = response?.newCalls?.[0];
        if (newCall?.id) {
          upsertNewCallInActiveCache(newCall);
          processQueuedEvents(channelId);
        } else {
          // Call didn't match current filters — discard pending state
          channelStateMap.current.delete(channelId);
          invalidateOtherListCallsQueries();
        }
      } catch (error) {
        // Clean up on failure — discard queued events
        channelStateMap.current.delete(channelId);

        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: 'Failed to fetch new call record on websocket event',
          addContext: {
            name: 'useCallRecordsWebsocketSubscription',
            context: {
              channelId,
              requestPayload,
            },
          },
        });
      }
    },
    [listCallsPayload, fetchListCalls, invalidateOtherListCallsQueries, processQueuedEvents, upsertNewCallInActiveCache]
  );

  const handleEvent = useCallback(
    async (event: CallRecordUpdateEvent) => {
      if (!enabled) {
        return;
      }

      if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_NEW) {
        const channelId = event.callIntelInsight?.channelId;
        if (!channelId) {
          return;
        }

        // If the current sort is not by descending start time, we cannot determine where to
        // place the new call record in the list, so we skip updating the cache but invalidate
        // other queries to ensure the new record is fetched in those lists
        if (listCallsPayload.sortType && listCallsPayload.sortType !== CallIntelTypes.SortType.SORT_DESC) {
          invalidateOtherListCallsQueries();
          return;
        }

        // Check if the new call record already exists in the cache before making the API call
        const activeQueryData = queryClient.getQueryData<InfiniteData<GetCallsResponse> | undefined>(activeQueryKey);
        const callExistsInCache = activeQueryData?.pages.some((page) =>
          page.newCalls?.some((call) => call.id === channelId)
        );

        if (callExistsInCache) {
          return;
        }

        // If call-intelligence analysis filters are active (e.g. sentiment, categories),
        // the call record hasn't been fully analyzed yet — the filter-match API would get
        // incomplete data and may incorrectly exclude the record. Defer the fetch to
        // CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_FINISHED_PROCESSING when analysis is complete.
        if (isCallIntelAnalysisFiltersApplied) {
          channelStateMap.current.set(channelId, {
            status: 'awaiting_analysis',
            queuedEvents: [],
          });
          return;
        }

        // No analysis filters — safe to fetch now. Track as 'fetching' so concurrent
        // events for this channelId are queued and replayed after the call is in cache.
        channelStateMap.current.set(channelId, {
          status: 'fetching',
          queuedEvents: [],
        });

        await fetchAndInsertCall(channelId);
        return;
      }

      if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_FINISHED_PROCESSING) {
        const callIntelInsight = event.callIntelInsight;
        if (!callIntelInsight?.channelId) {
          return;
        }

        const channelId = callIntelInsight.channelId;
        const state = channelStateMap.current.get(channelId);

        if (state?.status === 'awaiting_analysis') {
          // Analysis complete — now safe to fetch with all filters since the record has full data.
          channelStateMap.current.set(channelId, {
            ...state,
            status: 'fetching',
          });

          // Queue this event so the analysis insight data is applied after the call is in cache
          state.queuedEvents.push({ type: 'call_record', event });

          await fetchAndInsertCall(channelId);
          return;
        }

        if (state?.status === 'fetching') {
          // Filter-match fetch is in-flight — queue this event so it's replayed after the call is in cache
          state.queuedEvents.push({ type: 'call_record', event });
          return;
        }

        // No pending state — call is already in cache, apply the update directly
        updateCallInActiveCache(channelId, (call) => ({
          ...call,
          ...callIntelInsight,
        }));
        invalidateOtherListCallsQueries();
        invalidateCallMetadataQuery(channelId);
        return;
      }

      if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_FOLLOWUP_TASKS_PROCESSED) {
        const tasksCount = event.tasksCount;
        if (!tasksCount?.channelId) {
          return;
        }

        const channelId = tasksCount.channelId;
        const state = channelStateMap.current.get(channelId);

        // If the channelId is being tracked (awaiting analysis or in-flight fetch),
        // queue the event for replay after the call is inserted into cache
        if (state) {
          state.queuedEvents.push({ type: 'call_record', event });
          return;
        }

        // Call is already in cache — apply the update directly
        updateCallInActiveCache(channelId, (call) => ({
          ...call,
          taskCount: tasksCount.count ?? 0,
        }));
        invalidateOtherListCallsQueries();
        invalidateCallMetadataQuery(channelId);
        invalidateListTasksQueries(channelId);
        return;
      }
    },
    [
      listCallsPayload,
      activeQueryKey,
      enabled,
      fetchAndInsertCall,
      invalidateCallMetadataQuery,
      invalidateListTasksQueries,
      invalidateOtherListCallsQueries,
      isCallIntelAnalysisFiltersApplied,
      queryClient,
      updateCallInActiveCache,
    ]
  );

  const handleInboxTagEvent = useCallback(
    (event: InboxThreadItemTagAlert) => {
      if (!enabled) {
        return;
      }

      // Only handle call record and voicemail entity types — SMS and robocall are handled by the inbox subscription
      const isCallOrVoicemail =
        event.entityType === 'CALL_RECORD' ||
        event.entityType === 'ENTITY_TYPE_CALL' ||
        event.entityType === 'ENTITY_TYPE_VOICEMAIL';

      if (!isCallOrVoicemail) {
        return;
      }

      const channelId = event.entityId;
      if (!channelId) {
        return;
      }

      const state = channelStateMap.current.get(channelId);

      // If the channelId is being tracked (fetching or awaiting analysis), queue for replay
      if (state) {
        state.queuedEvents.push({ type: 'inbox_tag', event });
        return;
      }

      // Compute new tag list from current cache state and delegate update + panel sync to onTagUpdate
      const currentData = queryClient.getQueryData<InfiniteData<GetCallsResponse>>(activeQueryKey);
      const existingCall = currentData?.pages.flatMap((p) => p.newCalls ?? []).find((c) => c.id === channelId);
      const currentTagIds = existingCall?.tagIds ?? [];
      const updatedTagIds =
        event.updateType === 'ADDED'
          ? Array.from(new Set([...currentTagIds, event.tagId]))
          : currentTagIds.filter((id) => id !== event.tagId);
      onTagUpdate?.(channelId, updatedTagIds);
    },
    [activeQueryKey, enabled, onTagUpdate, queryClient]
  );

  const handleTagEvent = useCallback(
    (event: CallRecordTagNotification) => {
      if (!enabled) {
        return;
      }

      const channelId = event.channelId;
      if (!channelId) {
        return;
      }

      const state = channelStateMap.current.get(channelId);

      // If the channelId is being tracked, queue the tag event for replay
      if (state) {
        state.queuedEvents.push({ type: 'tag', event });
        return;
      }

      const updatedTags = event.tagsPayload.map((tag) => tag.id);
      onTagUpdate?.(channelId, updatedTags);
    },
    [enabled, onTagUpdate]
  );

  // Clean up stale pending channelIds when activeQueryKey changes
  // (filters/sort changed → old state map is irrelevant)
  useEffect(() => {
    channelStateMap.current.clear();
  }, [listCallsPayload]);

  useWebsocketEventSubscription(NotificationType.CALL_RECORD, (payload) => {
    handleEvent(payload.params);
  });

  useWebsocketEventSubscription(NotificationType.CALL_RECORD_TAG, (payload) => {
    handleTagEvent(payload.params);
  });

  useWebsocketEventSubscription(NotificationType.INBOX_THREAD_ITEM_TAG, (payload) => {
    handleInboxTagEvent(payload.params);
  });
};
